import { X } from '@phosphor-icons/react';
import { Dialog as RDialog } from 'radix-ui';
import { Link, type LinkProps } from 'react-router';
import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';

export function cx(...c: (string | false | null | undefined)[]) {
  return c.filter(Boolean).join(' ');
}

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type Size = 'sm' | 'md' | 'lg';

const variants: Record<Variant, string> = {
  primary:
    'bg-accent text-accent-ink hover:bg-accent-strong shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] disabled:bg-ink-700 disabled:text-fg-subtle',
  secondary:
    'bg-ink-800 text-fg border border-white/[0.08] hover:bg-ink-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] disabled:text-fg-subtle',
  outline: 'border border-white/15 text-fg hover:bg-white/[0.06] disabled:text-fg-subtle',
  ghost: 'text-fg-muted hover:text-fg hover:bg-white/[0.06] disabled:text-fg-subtle',
  danger: 'bg-danger/15 text-[#f3a3a4] border border-danger/30 hover:bg-danger/25',
};
const sizes: Record<Size, string> = {
  sm: 'h-8 min-w-8 px-3 text-[13px] gap-1.5 rounded-lg',
  md: 'h-10 min-w-10 px-4 text-sm gap-2 rounded-xl',
  lg: 'h-12 min-w-12 px-5 text-[15px] gap-2 rounded-xl',
};

const buttonBase =
  'inline-flex select-none items-center justify-center whitespace-nowrap font-medium tracking-tight transition-[background-color,color,transform,opacity] duration-200 ease-out active:scale-[0.98]';

/** A navigation link styled as a button (never nest a <button> inside an <a>). */
export function LinkButton({
  variant = 'secondary',
  size = 'md',
  className,
  ...rest
}: LinkProps & { variant?: Variant; size?: Size }) {
  return <Link className={cx(buttonBase, variants[variant], sizes[size], className)} {...rest} />;
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex select-none items-center justify-center whitespace-nowrap font-medium tracking-tight transition-[background-color,color,transform,opacity] duration-200 ease-out active:scale-[0.98] disabled:cursor-not-allowed disabled:active:scale-100',
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span className="h-3.5 w-3.5 rounded-full border-2 border-current border-r-transparent motion-safe:animate-spin" />
      ) : null}
      {children}
    </button>
  );
});

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: (id: string, describedBy: string | undefined) => ReactNode;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-[13px] font-medium text-fg">
        {label}
      </label>
      {children(id, describedBy)}
      {hint && !error && (
        <p id={hintId} className="text-xs text-fg-subtle">
          {hint}
        </p>
      )}
      {error && (
        <p id={errId} role="alert" className="text-xs text-[#f3a3a4]">
          {error}
        </p>
      )}
    </div>
  );
}

const inputCls =
  'w-full rounded-xl border border-white/10 bg-ink-900 px-3.5 text-sm text-fg placeholder:text-fg-subtle transition-colors focus:border-accent/60 focus:outline-none focus-visible:outline-none focus:ring-2 focus:ring-accent/25 aria-[invalid=true]:border-danger/60';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cx(inputCls, 'h-10', className)} {...rest} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cx(inputCls, 'min-h-24 py-2.5', className)} {...rest} />;
});

export function Select({
  value,
  onChange,
  options,
  id,
  ariaLabel,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  id?: string;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <select
      id={id}
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cx(inputCls, 'h-10 appearance-none pr-8', className)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden="true" className={cx('cs-skeleton rounded-xl', className)} />;
}

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'accent' | 'woot' | 'meh' | 'warn' | 'ok' | 'danger';
  className?: string;
}) {
  const tones = {
    neutral: 'bg-white/[0.06] text-fg-muted border-white/10',
    accent: 'bg-accent/12 text-accent-strong border-accent/25',
    woot: 'bg-woot/12 text-woot border-woot/25',
    meh: 'bg-meh/12 text-meh border-meh/25',
    warn: 'bg-warn/12 text-warn border-warn/25',
    ok: 'bg-ok/12 text-ok border-ok/25',
    danger: 'bg-danger/12 text-[#f3a3a4] border-danger/25',
  } as const;
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium tracking-wide',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function EmptyState({
  title,
  body,
  action,
  art,
  className,
}: {
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  art?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'flex flex-col items-start gap-3 rounded-2xl border border-dashed border-white/10 p-6',
        className,
      )}
    >
      {art}
      <h3 className="text-base font-semibold tracking-tight text-fg">{title}</h3>
      {body && <div className="max-w-[52ch] text-sm leading-relaxed text-fg-muted">{body}</div>}
      {action}
    </div>
  );
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  wide,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-40 bg-ink-950/70 backdrop-blur-sm" />
        <RDialog.Content
          className={cx(
            'fixed left-1/2 top-1/2 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-3xl border border-white/10 bg-ink-850 p-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.06)] cs-scroll',
            wide ? 'max-w-2xl' : 'max-w-md',
          )}
        >
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <RDialog.Title className="text-lg font-semibold tracking-tight">
                {title}
              </RDialog.Title>
              {description ? (
                <RDialog.Description className="mt-1 text-sm text-fg-muted">
                  {description}
                </RDialog.Description>
              ) : (
                <RDialog.Description className="sr-only">{title}</RDialog.Description>
              )}
            </div>
            <RDialog.Close asChild>
              <button
                className="-mr-2 -mt-1 rounded-lg p-2 text-fg-muted hover:bg-white/5 hover:text-fg"
                aria-label="Close"
              >
                <X size={18} weight="bold" />
              </button>
            </RDialog.Close>
          </div>
          {children}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '--:--';
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`;
}

export function timeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const d = Date.now() - Date.parse(iso);
  if (d < 60_000) return 'just now';
  if (d < 3_600_000) return `${Math.floor(d / 60_000)}m ago`;
  if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h ago`;
  return `${Math.floor(d / 86_400_000)}d ago`;
}
